import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Spinner } from '@components/index';
import InfiniteScroll from '@components/infinite-scroll/InfiniteScroll';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { UploadStatus } from '@features/lead-management/constants/leadFileUpload';
import {
  DocumentItem,
  SelectedFileItem
} from '@features/lead-management/types/leadFileUpload';
import { Note } from '@features/lead-management/types/leadNote';
import useNotify from '@hooks/notify';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';
import { getLocalStorageItem } from '@utils/storage';

import DeleteConfirmationModal from './DeleteConfirmationModal';
import NoteItem from './NoteItem';
import NotesInput from './NotesInput.';

import { LEAD_ACTIVITY_LIMIT } from '../../constants/leadActivity';
import leadFileUploadHook from '../../hooks/useLeadFileUpload';
import {
  useCreateLeadNoteMutation,
  useDeleteLeadNoteMutation,
  useLazyGetFileUrlQuery,
  useLazyGetLeadNotesQuery,
  useUpdateLeadNoteMutation
} from '../../queries/LeadManagementQuery';

type DocumentMap = Record<string, DocumentItem>;

type NoteWithDocumentMap = {
  id: string;
  note: string;
  created_at: string;
  updated_at?: string;
  owner: {
    name: string;
  };
  documents: DocumentMap;
};

type Notes = Record<string, NoteWithDocumentMap>;

const NotesTab: React.FC<{ leadId: string }> = ({ leadId }) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg } = useSelector((state: any) => state.user);
  const { createErrorAlert, createSuccessAlert } = useNotify();
  const [noteText, setNoteText] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [mappedNotes, setMappedNotes] = useState<Notes>({});
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editHasInteracted, setEditHasInteracted] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState<number>(0);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [allNotesData, setAllNotesData] = useState<Note[]>([]);
  const [isPaginationLoading, setIsPaginationLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const [getNotes, { isLoading, isFetching }] = useLazyGetLeadNotesQuery();
  const [getFileUrl] = useLazyGetFileUrlQuery();
  const [createNote] = useCreateLeadNoteMutation();
  const [deleteNote] = useDeleteLeadNoteMutation();
  const [updateNote] = useUpdateLeadNoteMutation();
  const {
    items: newFiles,
    addFiles,
    removeItem,
    reset
  } = leadFileUploadHook({
    organizationId,
    onError: (message) => createErrorAlert(message)
  });

  const {
    items: editFiles,
    addFiles: addEditFiles,
    removeItem: removeEditItem,
    reset: resetEditFiles,
    setItems: setEditFiles
  } = leadFileUploadHook({
    organizationId,
    onError: (message) => createErrorAlert(message)
  });

  const handleGetNotes = useCallback(
    async (isPagination: boolean) => {
      if (isPagination) {
        setIsPaginationLoading(true);
      }

      try {
        const res = await getNotes({
          organizationId: organizationId || '',
          leadId,
          page: isPagination ? page : 0,
          limit: LEAD_ACTIVITY_LIMIT
        });

        if (res.data) {
          const newNotesData = res.data.data ?? [];

          if (isPagination) {
            setIsPaginationLoading(false);
          }

          setAllNotesData((previousData) => {
            if (isPagination) {
              return [...previousData, ...newNotesData];
            }
            return newNotesData;
          });

          setDisablePagination(res.data?.pagination?.last_page === true);
          setError(null);
        }
      } catch (loadError) {
        if (isPagination) {
          setIsPaginationLoading(false);
        }
        setError(loadError);
        createErrorAlert('Failed to load notes. Please try again.');
      }
    },
    [getNotes, organizationId, leadId, page, createErrorAlert]
  );

  const handleRefetch = useCallback(() => {
    setPage(0);
    setAllNotesData([]);
    setDisablePagination(false);
    setError(null);
    handleGetNotes(false);
  }, [handleGetNotes]);

  const handleSave = async () => {
    try {
      const documents = newFiles
        .filter((f) => f.status === UploadStatus.COMPLETED)
        .map((f) => ({
          file_name: f.name,
          file_size: f.sizeLabel?.toString() || '',
          object_key: f.objectKey
        }));

      const noteResponse = await createNote({
        organizationId,
        leadId,
        payload: { note: noteText.replace(/[\r\n]+$/g, ''), documents }
      }).unwrap();

      sendMixpanelEvent(Event.LEAD_NOTE_CREATED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        note_id: noteResponse?.data?.id,
        note_length: noteText.trim().length
      });

      createSuccessAlert('Note created successfully');
      setNoteText('');
      setHasInteracted(false);
      reset();
      handleRefetch();
    } catch (e) {
      createErrorAlert('Error creating note');
    }
  };

  const handleCancel = () => {
    setNoteText('');
    setHasInteracted(false);
  };

  const handleAttachFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleAttachEditFile = useCallback(() => {
    editFileInputRef.current?.click();
  }, []);

  const fileActions = useCallback(
    async (
      file: SelectedFileItem,
      noteId: string,
      action: (url: string, name: string) => void
    ) => {
      try {
        const currentNote = mappedNotes[noteId];
        const currentDocument = currentNote?.documents[file.id] ?? null;
        if (currentDocument?.url) {
          action(currentDocument.url, file.name);
        } else {
          const response = await getFileUrl({
            organizationId,
            documentId: file.id
          });
          if (response.data.url) {
            action(response.data.url, file.name);
            setMappedNotes((prev: Notes) => ({
              ...prev,
              [noteId]: {
                ...prev[noteId],
                documents: {
                  ...prev[noteId].documents,
                  [file.id]: {
                    ...prev[noteId].documents[file.id],
                    url: response.data.url
                  }
                }
              }
            }));
          }
        }
      } catch (e) {
        createErrorAlert('Error handling file action');
      }
    },
    [mappedNotes, getFileUrl, organizationId, createErrorAlert]
  );

  const viewFile = useCallback(
    async (url: string, name: string) => {
      try {
        window.open(url, '_blank');
      } catch (e) {
        createErrorAlert(`Error viewing ${name}`);
      }
    },
    [createErrorAlert]
  );

  const handleViewFile = useCallback(
    async (file: SelectedFileItem, noteId: string) => {
      fileActions(file, noteId, viewFile);
    },
    [fileActions, viewFile]
  );

  const handleDownloadFile = useCallback(
    async (file: SelectedFileItem, noteId: string) => {
      fileActions(file, noteId, downloadFileFromUrlUsingBlob);
    },
    [fileActions]
  );

  const handleStartEdit = useCallback(
    (note: NoteWithDocumentMap) => {
      setEditingNoteId(note.id);
      setEditText(note.note);
      setEditFiles(
        Object.values(note.documents).map((doc) => ({
          id: doc.id || '',
          name: doc.file_name,
          sizeLabel: doc.file_size,
          status: UploadStatus.COMPLETED,
          url: doc.url
        }))
      );
      setEditHasInteracted(true);
    },
    [setEditFiles]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditText('');
    setEditHasInteracted(false);
    resetEditFiles();
  }, [resetEditFiles]);

  const handleSaveEdit = useCallback(async () => {
    try {
      const documents: {
        file_name: string;
        file_size: string;
        object_key: string;
      }[] = editFiles
        .filter(
          (f) => f.status === UploadStatus.COMPLETED && f.id.startsWith('temp_')
        )
        .map((f) => ({
          file_name: f.name,
          file_size: f.sizeLabel?.toString() || '',
          object_key: f.objectKey || ''
        }));

      const documentsToDelete =
        editingNoteId && mappedNotes[editingNoteId]
          ? Object.values(mappedNotes[editingNoteId].documents)
              .filter(
                (doc: any) =>
                  doc &&
                  !editFiles.some((f) => f.id === (doc as { id?: string }).id)
              )
              .map((doc: any) => (doc && (doc as { id?: string }).id) || '')
          : [];

      await updateNote({
        organizationId,
        leadId,
        payload: {
          id: editingNoteId || '',
          note: editText,
          documents_to_add: documents,
          documents_to_delete: documentsToDelete
        }
      }).unwrap();

      sendMixpanelEvent(Event.LEAD_NOTE_UPDATED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        note_id: editingNoteId || ''
      });
      handleCancelEdit();
      createSuccessAlert('Note updated successfully');
      handleRefetch();
    } catch (e) {
      createErrorAlert('Error updating note');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editFiles,
    editingNoteId,
    mappedNotes,
    updateNote,
    organizationId,
    leadId,
    editText,
    handleCancelEdit,
    createSuccessAlert,
    handleRefetch,
    createErrorAlert
  ]);

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote({ organizationId, leadId, noteId });
      sendMixpanelEvent(Event.LEAD_NOTE_DELETED, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        note_id: noteId
      });
      createSuccessAlert('Note deleted successfully');
      handleRefetch();
    } catch (deleteError) {
      createErrorAlert('Error deleting note');
    }
  };

  const handleDeleteClick = useCallback((noteId: string) => {
    setNoteToDelete(noteId);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (noteToDelete) {
      await handleDeleteNote(noteToDelete);
      setShowDeleteModal(false);
      setNoteToDelete(null);
      handleRefetch();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  const handleScroll = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  useEffect(() => {
    handleGetNotes(page !== 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const hasTrackedNotes = useRef(false);
  useEffect(() => {
    handleGetNotes(false);
    setPage(0);
    // Mixpanel event for notes viewed
    if (hasTrackedNotes.current) return;
    hasTrackedNotes.current = true;
    sendMixpanelEvent(Event.LEAD_NOTES_VIEWED, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      lead_id: leadId,
      total_notes: allNotesData.length
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, organizationId]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const handleFocus = () => setHasInteracted(true);
      textarea.addEventListener('focus', handleFocus);
      return () => textarea.removeEventListener('focus', handleFocus);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!allNotesData) return;
    const noteData: Notes = {};
    allNotesData.forEach((noteItem) => {
      const noteDocuments: DocumentMap = {};
      (noteItem.documents || []).forEach((doc: DocumentItem) => {
        noteDocuments[doc.id || ''] = doc;
      });
      noteData[noteItem.id] = { ...noteItem, documents: noteDocuments };
    });
    setMappedNotes(noteData);
  }, [allNotesData]);

  const renderNotesContent = useCallback(() => {
    if (
      (isLoading || isFetching) &&
      !isPaginationLoading &&
      allNotesData.length === 0
    ) {
      return <Spinner className="size-6" />;
    }

    if (error) {
      return (
        <div className="py-8 text-center">
          <p className="mb-4 text-red-500">
            Failed to load notes. Please try again.
          </p>
          <button
            type="button"
            onClick={handleRefetch}
            className="bg-blue-600 hover:bg-blue-700 rounded-md px-4 py-2 text-white"
          >
            Retry
          </button>
        </div>
      );
    }

    if (Object.values(mappedNotes).length > 0) {
      return (
        <div>
          <div className="flex flex-col gap-4">
            {Object.values(mappedNotes).map(
              (note: NoteWithDocumentMap, index: number) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  index={index}
                  totalNotes={Object.values(mappedNotes).length}
                  editingNoteId={editingNoteId}
                  editText={editText}
                  editHasInteracted={editHasInteracted}
                  editFiles={editFiles}
                  editTextareaRef={editTextareaRef}
                  editFileInputRef={editFileInputRef}
                  onStartEdit={handleStartEdit}
                  onDeleteClick={handleDeleteClick}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onSetEditText={setEditText}
                  onSetEditHasInteracted={setEditHasInteracted}
                  onAddEditFiles={addEditFiles}
                  onRemoveEditItem={(fileId) => removeEditItem(fileId)}
                  onAttachEditFile={handleAttachEditFile}
                  onViewFile={handleViewFile}
                  onDownloadFile={handleDownloadFile}
                />
              )
            )}
          </div>
          <InfiniteScroll
            handleScroll={handleScroll}
            isDisable={disablePagination}
            isLoading={isPaginationLoading}
            spinnerStyle="w-6 h-6"
          />
        </div>
      );
    }

    return null;
  }, [
    isLoading,
    isFetching,
    isPaginationLoading,
    allNotesData,
    mappedNotes,
    error,
    disablePagination,
    editingNoteId,
    editText,
    editHasInteracted,
    editFiles,
    handleRefetch,
    handleScroll,
    handleStartEdit,
    handleDeleteClick,
    handleCancelEdit,
    handleSaveEdit,
    addEditFiles,
    removeEditItem,
    handleAttachEditFile,
    handleViewFile,
    handleDownloadFile
  ]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5 rounded-xl bg-white p-6">
        <NotesInput
          noteText={noteText}
          setNoteText={setNoteText}
          hasInteracted={hasInteracted}
          setHasInteracted={setHasInteracted}
          textareaRef={textareaRef}
          fileInputRef={fileInputRef}
          newFiles={newFiles}
          addFiles={addFiles}
          removeItem={(file) => removeItem(file.id)}
          handleAttachFile={handleAttachFile}
          handleCancel={handleCancel}
          handleSave={handleSave}
        />
      </div>

      {/* Notes Timeline */}
      <div className="custom-scroll max-h-125 overflow-y-auto rounded-xl bg-white px-6 pb-4">
        {renderNotesContent()}
      </div>

      {showDeleteModal && (
        <DeleteConfirmationModal
          showDeleteModal={showDeleteModal}
          title="Delete Note"
          message="Are you sure you want to delete this note? This action cannot be undone."
          handleConfirmDelete={handleConfirmDelete}
          handleCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default NotesTab;
